import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Heart, Plane, Building2 } from "lucide-react";
import api from "../lib/api";
import { Spinner } from "../components/common";
import { Linkify, OfficialBadge } from "../components/DiscussionBoard";
import LinkPreviews from "../components/LinkPreviewCard";
import TripAutoHero from "../components/TripAutoHero";

function timeAgo(iso) {
  try {
    const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function Community() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/community/feed").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);

  return (
    <div data-testid="community-feed">
      <section className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="travellers airport friends conversation community" alt="Traveller community" className="absolute inset-0 w-full h-full" testid="community-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-950/90 via-fuchsia-900/75 to-rose-700/55" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-fuchsia-200 mb-3 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Community</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">The travel wall</h1>
          <p className="text-white/90 mt-4 max-w-2xl text-lg">The latest from travellers across every airline and airport. Jump into any page to join the conversation.</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
        {!items ? <Spinner /> : items.length === 0 ? (
          <p className="text-muted-foreground">No posts yet. Be the first to post on an airline or airport page.</p>
        ) : (
          <div className="space-y-4">
            {items.map((p) => {
              const to = p.entity_type === "airline" ? `/airlines/${p.entity_id}` : `/airports/${p.entity_id}`;
              return (
                <div key={p.id} className={`border bg-white p-4 ${p.is_official ? "border-sky-300 bg-sky-50/30" : "border-zinc-300"}`} data-testid={`feed-post-${p.id}`}>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="font-700">{p.user_name || "Traveller"}</span>
                    {p.is_official && <OfficialBadge label={p.official_label} />}
                    <span className="text-muted-foreground">· {timeAgo(p.created_at)}</span>
                    {p.entity_name && (
                      <Link to={to} className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        {p.entity_type === "airline" ? <Plane className="w-3.5 h-3.5 -rotate-45" /> : <Building2 className="w-3.5 h-3.5" />} {p.entity_name}
                      </Link>
                    )}
                  </div>
                  <p className="text-base text-zinc-800 mt-2 whitespace-pre-line"><Linkify text={p.text} /></p>
                  <LinkPreviews text={p.text} />
                  {p.image_url && <img src={p.image_url} alt="" className="mt-3 rounded border border-zinc-200 max-h-96 object-cover" />}
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><Heart className="w-4 h-4" /> {p.like_count}</span>
                    <Link to={to} className="inline-flex items-center gap-1.5 hover:text-primary"><MessageCircle className="w-4 h-4" /> Join</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
